import { getAccessToken } from "../config/popupAuth";
import { getSelectedEnvironment } from "./environmentStorage";
import { getApiUrlForEnvironment } from "../config/apiEnvironments";
import { getStorageAdapter } from "./storageService";
import logger from "../../logger";

const STORAGE_KEYS = {
  ORGANIZATIONS: "novataOrganizations",
  PERMISSIONS: "novataPermissions",
  SELECTED_ORG_ID: "selectedOrganizationId",
  SELECTED_ORG_UNIT_ID: "selectedOrganizationUnitId",
};

const getStorage = () => getStorageAdapter();

export const fetchAndStoreOrganizations = async () => {
  try {
    const accessToken = await getAccessToken();

    if (!accessToken) {
      throw new Error("Access token not available");
    }

    const environment = await getSelectedEnvironment();
    const API_BASE_URL = getApiUrlForEnvironment(environment);
    const url = `${API_BASE_URL}/apis/novata/me`;

    const response = await fetch(url, {
      method: "GET",
      headers: {
        accept: "*/*",
        authorization: `Bearer ${accessToken}`,
        "X-Novata-App": "NOVATA",
        "novata-app": "EXCEL_ADDIN",
        "content-type": "application/json",
      },
      mode: "cors",
      credentials: "omit",
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status} ${response.statusText}`);
    }

    const responseData = await response.json();

    let organizationsArray = [];

    if (responseData?.data?.organizations && Array.isArray(responseData.data.organizations)) {
      organizationsArray = responseData.data.organizations;
    } else if (responseData && Array.isArray(responseData)) {
      organizationsArray = responseData;
    } else if (responseData?.organizations && Array.isArray(responseData.organizations)) {
      organizationsArray = responseData.organizations;
    }

    const permissionsMap = {};
    organizationsArray.forEach((org) => {
      if (org.id && org.permissions && Array.isArray(org.permissions)) {
        permissionsMap[org.id] = org.permissions;
      }
    });

    const storage = getStorage();
    await storage.setItem(STORAGE_KEYS.ORGANIZATIONS, JSON.stringify(organizationsArray));
    await storage.setItem(STORAGE_KEYS.PERMISSIONS, JSON.stringify(permissionsMap));

    return {
      organizations: organizationsArray,
      permissions: permissionsMap,
    };
  } catch (error) {
    logger.error("Error fetching organizations:", error);
    throw error;
  }
};

export const getStoredOrganizations = async () => {
  try {
    const storage = getStorage();
    const organizationsJson = await storage.getItem(STORAGE_KEYS.ORGANIZATIONS);

    if (!organizationsJson) {
      return [];
    }

    return JSON.parse(organizationsJson);
  } catch (error) {
    logger.error("Error retrieving organizations from storage:", error);
    return [];
  }
};

export const getOrganizationPermissions = async (orgId) => {
  try {
    if (!orgId) {
      return [];
    }

    const storage = getStorage();
    const permissionsJson = await storage.getItem(STORAGE_KEYS.PERMISSIONS);

    if (!permissionsJson) {
      return [];
    }

    const permissionsMap = JSON.parse(permissionsJson);
    return permissionsMap[orgId] || [];
  } catch (error) {
    logger.error("Error retrieving permissions from storage:", error);
    return [];
  }
};

export const getSelectedOrganization = async () => {
  try {
    const storage = getStorage();
    const selectedOrgId = await storage.getItem(STORAGE_KEYS.SELECTED_ORG_ID);

    if (!selectedOrgId) {
      return null;
    }

    const organizations = await getStoredOrganizations();
    return organizations.find((org) => org.id === selectedOrgId) || null;
  } catch (error) {
    logger.error("Error retrieving selected organization:", error);
    return null;
  }
};

export const setSelectedOrganization = async (orgId) => {
  try {
    const storage = getStorage();
    if (orgId) {
      await storage.setItem(STORAGE_KEYS.SELECTED_ORG_ID, orgId);
    } else {
      await storage.removeItem(STORAGE_KEYS.SELECTED_ORG_ID);
    }
  } catch (error) {
    logger.error("Error setting selected organization:", error);
  }
};

export const fetchAndStoreOrganizationUnitId = async (orgId) => {
  try {
    const accessToken = await getAccessToken();
    if (!accessToken) {
      throw new Error("Access token not available");
    }

    const environment = await getSelectedEnvironment();
    const API_BASE_URL = getApiUrlForEnvironment(environment);
    const url = `${API_BASE_URL}/apis/novata/organization-units/hierarchy?organizationId=${encodeURIComponent(orgId)}`;

    const response = await fetch(url, {
      method: "GET",
      headers: {
        accept: "*/*",
        authorization: `Bearer ${accessToken}`,
        "X-Novata-App": "NOVATA",
        "novata-app": "EXCEL_ADDIN",
        "novata-organization-id": orgId,
        "content-type": "application/json",
      },
      mode: "cors",
      credentials: "omit",
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status} ${response.statusText}`);
    }

    const responseData = await response.json();

    let orgUnits = [];
    if (Array.isArray(responseData)) {
      orgUnits = responseData;
    } else if (responseData?.orgUnits && Array.isArray(responseData.orgUnits)) {
      orgUnits = responseData.orgUnits;
    } else if (responseData?.data && Array.isArray(responseData.data)) {
      orgUnits = responseData.data;
    }

    const rootUnit =
      orgUnits.find((unit) => unit.isRootOrgUnit && !unit.parentOrganizationUnitId) ||
      orgUnits.find((unit) => unit.isRootOrgUnit);
    const orgUnitId = rootUnit?.id || orgUnits[0]?.id || null;

    const storage = getStorage();
    if (orgUnitId) {
      await storage.setItem(STORAGE_KEYS.SELECTED_ORG_UNIT_ID, orgUnitId);
    } else {
      await storage.removeItem(STORAGE_KEYS.SELECTED_ORG_UNIT_ID);
    }

    return orgUnitId;
  } catch (error) {
    logger.error("Error fetching organization unit hierarchy:", error);
    return null;
  }
};

export const getSelectedOrganizationUnitId = async () => {
  try {
    const storage = getStorage();
    return (await storage.getItem(STORAGE_KEYS.SELECTED_ORG_UNIT_ID)) || null;
  } catch (error) {
    logger.error("Error retrieving selected organization unit ID:", error);
    return null;
  }
};

export const getSelectedOrganizationPermissions = async () => {
  try {
    const selectedOrg = await getSelectedOrganization();
    if (!selectedOrg) {
      return [];
    }
    return await getOrganizationPermissions(selectedOrg.id);
  } catch (error) {
    logger.error("Error retrieving selected organization permissions:", error);
    return [];
  }
};

export const clearOrganizationsStorage = async () => {
  try {
    const keys = Object.values(STORAGE_KEYS);

    if (typeof OfficeRuntime !== "undefined" && OfficeRuntime.storage) {
      await Promise.all(keys.map((k) => OfficeRuntime.storage.removeItem(k)));
    }

    keys.forEach((k) => localStorage.removeItem(k));
  } catch (error) {
    logger.error("Error clearing organizations storage:", error);
  }
};
